import { useCreateRoomType, useUpdateRoomType } from '@hooks/accommodation';
import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { AccommodationRoomType } from '../../../../types/accommodation';

interface Props {
  propertyId: string;
  editing: AccommodationRoomType | null;
  onClose: () => void;
}

export const RoomTypeFormModal: React.FC<Props> = ({
  propertyId,
  editing,
  onClose,
}) => {
  const createRoomType = useCreateRoomType();
  const updateRoomType = useUpdateRoomType();
  const isSubmitting = createRoomType.isPending || updateRoomType.isPending;

  const [form, setForm] = useState({
    name: '',
    description: '',
    capacity: 2,
    base_price: 0,
  });

  useEffect(() => {
    if (editing) {
      setForm({
        name: editing.name,
        description: editing.description ?? '',
        capacity: editing.capacity,
        base_price: editing.base_price,
      });
    }
  }, [editing]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name: form.name.trim(),
      description: form.description.trim() || null,
      capacity: form.capacity,
      base_price: form.base_price,
    };

    if (editing) {
      updateRoomType.mutate(
        { id: editing.id, updates: payload },
        { onSuccess: onClose },
      );
    } else {
      createRoomType.mutate(
        { property_id: propertyId, ...payload },
        { onSuccess: onClose },
      );
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h4 className="font-semibold text-gray-900">
            {editing ? 'Edit room type' : 'New room type'}
          </h4>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100"
          >
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              htmlFor="rtf-name"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              Name *
            </label>
            <input
              id="rtf-name"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              placeholder="e.g. Standard Double"
            />
          </div>

          <div>
            <label
              htmlFor="rtf-desc"
              className="mb-1 block text-xs font-medium text-gray-700"
            >
              Description
            </label>
            <textarea
              id="rtf-desc"
              rows={2}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                htmlFor="rtf-capacity"
                className="mb-1 block text-xs font-medium text-gray-700"
              >
                Capacity (guests)
              </label>
              <input
                id="rtf-capacity"
                type="number"
                min={1}
                value={form.capacity}
                onChange={(e) =>
                  setForm({ ...form, capacity: Number(e.target.value) })
                }
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              />
            </div>
            <div>
              <label
                htmlFor="rtf-price"
                className="mb-1 block text-xs font-medium text-gray-700"
              >
                Base price / night
              </label>
              <input
                id="rtf-price"
                type="number"
                min={0}
                step="0.01"
                value={form.base_price}
                onChange={(e) =>
                  setForm({ ...form, base_price: Number(e.target.value) })
                }
                className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-pos-primary/30"
              />
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="rounded-xl bg-pos-primary px-4 py-2 text-sm font-medium text-white hover:bg-pos-primary/90 disabled:opacity-60"
            >
              {isSubmitting ? 'Saving…' : editing ? 'Save changes' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
